import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { AlertTriangle, CheckCircle2, CircleDashed, Clock, PawPrint, Search } from "lucide-react";

import { speciesIconSrc } from "./AnimalOverview";
import "../pages/PleeggezinOverzicht.css";
import "../pages/PensionOverzichten.css";

/**
 * De opbouw van de asieloverzichten voor de dagelijkse pensionlijsten (Wegen, Bodycheck, Borstelen, Medicatie,
 * Voerlijst) — Maarten, 2026-10-05: "al die overzichten moeten de standaard stijl krijgen, die van asiel".
 * Een kop met overline, titel en de tellers als tegels rechts; daaronder een filterkaart met zoeken, de datum en de
 * snelfilters; dan het aantal en de lijst. De pagina's zelf houden hun eigen tabel en acties.
 */

export interface SoortOptie {
  sleutel: string;
  label: string;
  aantal: number;
}

/**
 * De kop zoals op Pensionoverzichten: rond icoon, grote titel met uitleg, en rechts de tegels van het dierenoverzicht
 * van het asiel. Eerst de diersoorten met hun plaatje (aanklikken filtert), dan de tellers van de lijst, en Totaal.
 */
export function PensionLijstKop({ icoon, titel, subtitel, soort, children }: {
  icoon: ReactNode;
  titel: string;
  subtitel: string;
  soort?: { opties: SoortOptie[]; actief: string; onKies: (sleutel: string) => void; totaal: number };
  children: ReactNode;
}) {
  const t = useTranslate();
  const tellers = Children.toArray(children).filter(isValidElement);
  return (
    <div className="po werklijst-kopvak">
      <header className="po-kop">
        <span className="po-kop__icoon" aria-hidden="true">{icoon}</span>
        <div className="po-kop__tekst">
          <h1>{titel}</h1>
          <p>{subtitel}</p>
        </div>
        <div className="da-overview__stats po-tellers" role="group">
          {soort?.opties.filter((o) => o.aantal > 0 || o.sleutel === soort.actief).map((o) => (
            <Tegel key={o.sleutel} icoon={<img src={speciesIconSrc(o.sleutel)} alt="" />} waarde={o.aantal} label={o.label}
              actief={soort.actief === o.sleutel} onClick={() => soort.onKies(soort.actief === o.sleutel ? "ALLE" : o.sleutel)} />
          ))}
          {tellers.map((el, i) =>
            cloneElement(el as ReactElement<{ icoon?: ReactNode }>, {
              icoon: (el.props as { icoon?: ReactNode }).icoon ?? TELLER_ICONEN[i % TELLER_ICONEN.length],
            }),
          )}
          {soort && (
            <Tegel icoon={<PawPrint size={20} />} waarde={soort.totaal} label={t("pensionOverzichten.teller.totaal")}
              actief={soort.actief === "ALLE"} onClick={() => soort.onKies("ALLE")} />
          )}
        </div>
      </header>
    </div>
  );
}

const TELLER_ICONEN = [<Clock size={20} />, <CheckCircle2 size={20} />, <AlertTriangle size={20} />, <CircleDashed size={20} />];

function Tegel({ icoon, waarde, label, toon, actief, onClick }: {
  icoon: ReactNode;
  waarde: number | undefined;
  label: string;
  toon?: "rood" | "groen";
  actief?: boolean;
  onClick?: () => void;
}) {
  const inhoud = (
    <>
      <span className="da-overview__stat-icon" aria-hidden="true">{icoon}</span>
      <span className="da-overview__stat-body">
        <span className="da-overview__stat-value">{waarde ?? "—"}</span>
        <span className="da-overview__stat-label">{label}</span>
      </span>
    </>
  );
  return onClick ? (
    <button type="button" className={`da-overview__stat${actief ? " da-overview__stat--active" : ""}`} data-toon={toon}
      aria-pressed={!!actief} onClick={onClick}>
      {inhoud}
    </button>
  ) : (
    <div className="da-overview__stat werklijst-teller" data-toon={toon}>{inhoud}</div>
  );
}

/** Een teller van de lijst (te wegen, gewogen, …) als tegel in de kop; het icoon volgt de volgorde, tenzij gegeven. */
export function PensionLijstTegel({ waarde, label, toon, icoon }: { waarde: number | undefined; label: string; toon?: "rood"; icoon?: ReactNode }) {
  return <Tegel icoon={icoon} waarde={waarde} label={label} toon={toon} />;
}

/**
 * De filterkaart zoals op Pensionoverzichten: bovenaan zoeken, de datum (met label) en de knoppen van de lijst;
 * daaronder de snelfilter als schakelbalk (soort of dagdeel) en rechts de keuze tussen de drie weergaven.
 */
export function PensionLijstFilters({ zoek, onZoek, rechts, snelfilters, snelLabel, weergave }: {
  zoek: string;
  onZoek: (z: string) => void;
  rechts: ReactNode;
  snelfilters?: ReactNode;
  snelLabel?: string;
  weergave?: ReactNode;
}) {
  const t = useTranslate();
  return (
    <section className="po-filters werklijst-filters" aria-label={t("pensionOverzichten.filters")}>
      <div className="po-filters__rij">
        <label className="po-zoek">
          <Search size={16} aria-hidden="true" />
          <input type="search" autoComplete="off" value={zoek} onChange={(e) => onZoek(e.target.value)} placeholder={t("pensionLijst.zoek")}
            aria-label={t("pensionLijst.zoek")} />
        </label>
        {rechts}
      </div>
      <div className="po-filters__rij po-filters__rij--tweede">
        {snelfilters && (
          <span className="po-groepkeuze">
            <span className="po-zacht">{snelLabel ?? t("pensionLijst.soort")}</span>
            {snelfilters}
          </span>
        )}
        <span className="po-vul" />
        {weergave}
      </div>
    </section>
  );
}

/** Het aantal boven de lijst, zoals "4 dieren gevonden" op Pensionoverzichten. */
export function PensionLijstMeta({ aantal }: { aantal: number }) {
  const t = useTranslate();
  return (
    <h2 className="po-aantal werklijst-aantal">
      {t(`pensionOverzichten.gevonden.dieren.${aantal === 1 ? "een" : "meer"}`, { args: [aantal] })}
    </h2>
  );
}

/** Zoeken op alles wat als tekst in de regel staat: dier, klant, verblijf, medicijn … */
export function zoekIn<R extends object>(regels: R[], zoek: string): R[] {
  const z = zoek.trim().toLowerCase();
  if (!z) return regels;
  return regels.filter((r) => Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(z)));
}
